import React from "react";
import Modal from "@components/atoms/Modal";
import { Diamond } from "@src/types/DiamondDetail";
import styled from "styled-components/native";
import LabelView from "@components/molecules/LabelView";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { isLinkedPair } from "@utils/product/productValidators";
import { Spacer, VDBText } from "@components/atoms";
import { CustomisationKey } from "../types";
import { useFilteredSection } from "../../../hooks/useFilteredOptions";
import { useGetPdpSectionLayout } from "../../../hooks/useGetSectionLayout";
import { PdpSections } from "@utils/customization";

const OwnerInfoModal = ({
  isOpen,
  onClose,
  itemDetail,
  productType,
}: {
  isOpen: boolean;
  onClose: () => void;
  itemDetail: Diamond;
  productType: string;
}) => {
  const { typography, t, theme, user } = useScreenOptionsHooks();
  const { deviceWidth: screenWidth } = useIsFoldable();
  const linkedPairItems = itemDetail.children;
  const priceMode = user?.price_mode;
  const sectionLayout = useGetPdpSectionLayout(
    PdpSections.SUMMARY,
    productType
  );
  const getFilterDetailData = (searchText: string): any => {
    const filteredArray = filteredSections.filter(
      (item) => item.name === searchText
    );

    return filteredArray;
  };
  const filteredSections = useFilteredSection(theme, priceMode, sectionLayout);
  const objStockNumber = getFilterDetailData(CustomisationKey.stock_no);
  const isStockNumberVisible =
    objStockNumber?.[0]?.price_modes?.includes(priceMode);
  const DefaultOwnerInfo = () => {
    return (
      <Container>
        {itemDetail?.owner_info &&
          Object.entries(itemDetail?.owner_info).map(
            ([fieldKey, fieldValue], index) => {
              if (!fieldKey) return null;
              const [key, value] = Object?.entries?.(fieldValue)[0] || [];

              return key && value !== undefined ? (
                <FieldContainer key={index}>
                  <LabelView
                    itemLeftText={key}
                    itemRightText={value || "-"}
                    cellWidth={theme.isTablet ? screenWidth / 6.5 : 220}
                    leftPostfixText=":"
                    isFixedKeyWidth={true}
                    keyWidth={150}
                    ellipsize={false}
                    keyTypographyStyle={typography["Body-R"]}
                    valueTypographyStyle={typography["Body-M"]}
                    isFixWidth
                  />
                </FieldContainer>
              ) : null;
            }
          )}
      </Container>
    );
  };

  const LinkedPairOwnerInfo = () => {
    if (!linkedPairItems?.length) return null;

    const ownerInfos = linkedPairItems.map((child) => child?.owner_info || {});
    const allKeys = Array.from(
      new Set(ownerInfos.flatMap((info) => Object.keys(info || {})))
    );
    const renderCard = (
      label: string,
      values: (string | number | null | undefined)[]
    ) => (
      <FieldCard key={label}>
        <KeyLabelContainer>
          <VDBText
            type={typography["Body-R"]}
            color={theme["core-06"]}
            textAlign="center"
          >
            {label}
          </VDBText>
        </KeyLabelContainer>

        <ValuesContainer>
          {values.map((value, idx) => (
            <ValueBox key={idx}>
              <VDBText
                type={typography["Body-M"]}
                color={theme["core-05"]}
                numberOfLines={1}
                textAlign="center"
              >
                {value ?? "-"}
              </VDBText>
            </ValueBox>
          ))}
        </ValuesContainer>
      </FieldCard>
    );

    return (
      <LinkedPairContainer>
        {isStockNumberVisible && (
          <>
            {renderCard(
              t("productDetailPage.stockNum.pair.text", "Stock#"),
              linkedPairItems.map((item) => item?.stock_num ?? "-")
            )}
            <Spacer size={SpaceType.Basic} />
          </>
        )}
        {allKeys.map((key, index) => {
          const fieldLabel =
            Object.entries(
              ownerInfos.find((info) => info[key])?.[key] || {}
            )[0]?.[0] || key;

          const values = ownerInfos.map((info) => {
            const entry = Object.entries(info[key] || {})[0];
            return entry ? entry[1] ?? "-" : "-";
          });

          return (
            <>
              {renderCard(fieldLabel, values)}{" "}
              {index < allKeys.length - 1 && <Spacer size={SpaceType.Basic} />}
            </>
          );
        })}
      </LinkedPairContainer>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      paddingLeft={0}
      paddingRight={0}
      paddingBottom={0}
      noPadding
      size={isLinkedPair(itemDetail) ? "md" : "sm"}
      type={theme.isMobile ? "sheet" : "popUp"}
      headerBackgroundColor={theme["core-01"]}
      headerTitle={t("productDetail.ownerInfo.modal.title.text")}
    >
      <Overlay itemDetail={itemDetail}>
        {isLinkedPair(itemDetail) ? (
          <LinkedPairOwnerInfo />
        ) : (
          <DefaultOwnerInfo />
        )}
      </Overlay>
    </Modal>
  );
};

const Overlay = styled.View<{ itemDetail: Diamond }>`
  background-color: ${({ theme, itemDetail }) =>
    isLinkedPair(itemDetail) ? theme["core-02"] : theme["core-03"]};
  padding-horizontal: ${({ theme, itemDetail }) =>
    isLinkedPair(itemDetail)
      ? `${SpaceType.Double}px`
      : !theme.isMobile
      ? `${SpaceType.ExtraLarge}px`
      : `${SpaceType.Double}px`};
  padding-top: ${({ theme, itemDetail }) =>
    isLinkedPair(itemDetail)
      ? `${SpaceType.Double}px`
      : !theme.isMobile
      ? `${SpaceType.ExtraLarge}px`
      : `${SpaceType.Big}px`};
  padding-bottom: ${({ itemDetail }) =>
    isLinkedPair(itemDetail)
      ? `${SpaceType.Double}px`
      : `${SpaceType.ExtraLarge}px`};
`;
const Container = styled.View`
  background-color: ${({ theme }) => theme["core-02"]};
  border-radius: ${SpaceType.Basic}px;
  padding: ${SpaceType.Double}px;
`;

const FieldContainer = styled.View`
  flex-direction: row;
  align-items: center;
  padding-vertical: 4px;
`;

const LinkedPairContainer = styled.ScrollView`
  flex-direction: column;
  border-radius: ${SpaceType.Basic}px;
`;

const FieldCard = styled.View`
  background-color: ${({ theme }) => theme["core-02"]};
  border-radius: ${SpaceType.Basic}px;
  overflow: hidden;
  border-width: 1px;
  border-radius: ${SpaceType.Basic}px;
  border-color: ${({ theme }) => theme["core-03"]};
`;

const KeyLabelContainer = styled.View`
  background-color: ${({ theme }) => theme["core-02"]};
  align-items: center;
  justify-content: center;
  padding-vertical: ${SpaceType.ExtraSmall}px;
  padding-horizontal: ${SpaceType.Basic}px;
`;

const ValuesContainer = styled.View`
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  background-color: ${({ theme }) => theme["core-01"]};
  padding: ${SpaceType.Basic}px;
  gap: ${SpaceType.Double}px;
`;

const ValueBox = styled.View`
  flex: 1;
  align-items: center;
  justify-content: center;
`;

export default OwnerInfoModal;
